import { DatePipe } from '@angular/common';
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CaseDetail } from '../cases/case-detail';
import { CaseService } from '../cases/case.service';
import { Case } from '../cases/models/case';
import { DialogAbsenceComponent } from '../dialogs/dialog-absence/dialog-absence.component';
import { DialogQueryComponent } from '../dialogs/dialog-query/dialog-query.component';
import { DialogTaskComponent } from '../dialogs/dialog-task/dialog-task.component';
import { DialogPresenter } from '../medi-dialog/DialogPresenter';
import { DialogType } from '../medi-dialog/medi-dialog.component';
import { EventTyp, OroService } from '../oroservice';
import { AccountBalance } from '../shared/models/account-balance';
import { AccountItem } from '../shared/models/account-item';
import { DateService } from '../shared/services/date.service';
import { DocumentService } from '../shared/services/document.service';
import { DashboardService } from './dashboard.service';
import { Absence } from './models/absence';
import { Document } from './models/document';
import { Question } from './models/question';
import { Task } from './models/task';

@Component({
    selector: 'Dashboard',
    templateUrl: './dashboard.html',
    styleUrls: ['./dashboard.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FormsModule,
        RouterLink,
        RouterLinkActive,
        TranslateModule,
        MatCardModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTableModule,
        MatTooltipModule,
    ],
    providers: [DashboardService]
})

export class Dashboard implements OnInit {
    private readonly oroservice = inject(OroService);
    private readonly dashboardService = inject(DashboardService);
    private readonly caseService = inject(CaseService);
    private readonly dateService = inject(DateService);
    private readonly documentService = inject(DocumentService);
    private readonly dialog = inject(DialogPresenter);

    absences: Absence[] = [];
    absenceColumns: string[] = ['Von', 'Bis', 'Plus'];
    accountDataSource: AccountItem[] = [];
    accountColumns: string[] = ['Key', 'Date', 'Value'];
    question: Question | null = null;
    questions: Question[] = [];
    questionCount: number = 0;
    task: Task | null = null;
    tasks: Task[] = [];
    taskCount: number = 0;
    documents: Document[] = [];
    accountBalance: AccountBalance | null = null;
    caseSearch: string = '';

    constructor() {
        this.oroservice.onChange.pipe(takeUntilDestroyed()).subscribe({
            next: async (event: EventTyp) => {
                if (event.component === this.constructor.name) {
                    await this.oroservice.getKundenInfo();
                }
            }
        });
    }

    public async ngOnInit(): Promise<void> {
        await this.oroservice.getKundenInfo();
        await this.refreshAbsences();
        await this.refreshDocuments();
        await this.refreshAccountBalance();
        await this.refreshTasks();
        await this.refreshQuestions();
    }

    // Tasks---------------------------------------------------------------------------------------------------------
    public async refreshTasks(): Promise<void> {
        const tasks: Task[] = await this.dashboardService.getTasks();
        this.tasks = tasks;
        if (this.tasks.length < 1) {
            this.task = null;
        } else {
            this.task = tasks[0];
        }
        this.taskCount = await this.dashboardService.getTaskCount();
    }

    public get showMasterDataCard(): boolean {
        return this.oroservice.kundeninfo.IsMaster === true && !this.areMasterDataValid();
    }

    public get hasQuestions(): boolean {
        return this.questions.length > 0;
    }

    public get hasTasks(): boolean {
        return this.tasks.length > 0;
    }

    private areMasterDataValid(): boolean {
        if (!this.oroservice.kundeninfo.Gps?.length) {
            return true;
        }
        const gps = this.oroservice.kundeninfo.Gps.filter(o => o.Id === this.oroservice.kundeninfo.Gp);
        if (gps.length > 0) {
            return Boolean(gps[0].Stammdatenvalide);
        } else {
            return true;
        }
    }

    public async selectTask(event: MouseEvent, task: Task): Promise<void> {
        if ((event.target as HTMLElement | null)?.id === 'dossier') {
            return;
        }
        this.task = task;
        await this.openTaskDialog();
    }

    // Questions---------------------------------------------------------------------------------------------------------
    public async refreshQuestions(): Promise<void> {
        this.questionCount = await this.dashboardService.getQuestionCount();
        const questions: Question[] = await this.dashboardService.getQuestions();
        this.questions = questions;
        if (this.questions.length < 1) {
            this.question = null;
        } else {
            this.questions.forEach((question: Question) => {
                if (question.Rechnungen?.length) {
                    question.Rechnungen.forEach((item: { Zahldatum: string | null }) => {
                        item.Zahldatum = new DatePipe('en-EN').transform(this.dateService.parseDate(item.Zahldatum ?? ''), 'yyyy-MM-dd');
                    });
                }
            });

            this.question = this.questions[0];
        }
    }

    public async refreshDocuments(): Promise<void> {
        const documents = await this.documentService.getDocuments<Document>();
        this.documents = documents;
        await this.documentService.refreshCount();
    }

    public async refreshAccountBalance(): Promise<void> {
        this.accountBalance = await this.dashboardService.getAccountBalance();
        this.accountDataSource = [];
        this.accountDataSource.push({ Key: 'Startseite.Kontostand', Date: this.accountBalance.Created, Value: "CHF " + this.accountBalance.Betrag.toFixed(2) });
    }

    public async downloadSelected(): Promise<void> {
        const unreadDocuments: Document[] = this.documents.filter((document: Document) => document.Gelesen === false);
        if (unreadDocuments.length < 1) return;
        await this.documentService.downloadZip(unreadDocuments);
        await this.refreshDocuments();
    }

    public async selectQuestion(event: MouseEvent, question: Question): Promise<void> {
        if ((event.target as HTMLElement | null)?.id === 'dossier') {
            return;
        }
        this.question = question;
        await this.openQuestionDialog();
    }

    // Absences---------------------------------------------------------------------------------------------------------
    public async refreshAbsences(): Promise<void> {
        this.absences = await this.dashboardService.getAbsences();
    }

    public async openDialogDeleteAbsence(absence: Absence): Promise<void> {
        const result = await this.dialog.showDialog({
            title: 'Startseite.Abwesenheitloeschen',
            content: 'Startseite.Abwesenheitloeschererklaerung',
            primary: 'Aufträge.Löschen',
            secondary: 'Abbrechen',
            type: DialogType.Question,
            width: '500px',
        });
        if (result) {
            await this.dashboardService.deleteAbsence(absence);
            await this.refreshAbsences();
        }
    }

    public async openAbsenceDialog(from: Date | null, until: Date | null, id: number): Promise<void> {
        const result = await this.dialog.showComponent<DialogAbsenceComponent, { von: Date | null; bis: Date | null; id: number }, boolean>(DialogAbsenceComponent, {
            width: "500px",
            disableClose: true,
            data: { von: from, bis: until, id: id }
        });

        if (result) {
            await this.refreshAbsences();
        }
    }

    public async editAbsence(absence: Absence | null): Promise<void> {
        if (absence != null) {
            await this.openAbsenceDialog(this.dateService.parseDate(absence.Von), this.dateService.parseDate(absence.Bis), absence.Id);
        } else {
            await this.openAbsenceDialog(null, null, 0);
        }
    }

    public async tryOpenCase(id: number = 0): Promise<void> {
        if (id === 0 && !this.caseSearch) {
            return;
        }
        const loadedCase = await this.caseService.getCase(id === 0 ? this.caseSearch : id);
        if (loadedCase != null) {
            this.openCaseDialog(loadedCase);
        } else {
            await this.dialog.showDialog({
                title: 'Achtung',
                content: 'UnbekannteDossiernummer',
                primary: 'OK',
                secondary: '',
                type: DialogType.Warning,
                width: '500px',
            });
        }
    }

    public openCaseDialog(selectedCase: Case): void {
        this.dialog.openComponent<CaseDetail, { dossierinfo: Case }, unknown>(CaseDetail, {
            width: "850px",
            disableClose: true,
            data: { dossierinfo: selectedCase },
            autoFocus: false
        });
    }

    public async openTaskDialog(): Promise<void> {
        const task: Task | null = this.task;
        if (task === null) return;
        const wasDone = task.Erledigt;
        await this.dialog.showComponent<DialogTaskComponent, { aufgabe: Task }, boolean>(DialogTaskComponent, {
            width: "800px",
            disableClose: true,
            data: { aufgabe: task },
            autoFocus: false
        });

        if (task.Erledigt !== wasDone) {
            await this.dashboardService.markTaskDone(task);
        }
        await this.refreshTasks();
    }

    public async openQuestionDialog(): Promise<void> {
        const question: Question | null = this.question;
        if (question === null) return;
        await this.dialog.showComponent<DialogQueryComponent, { frage: Question }, boolean>(DialogQueryComponent, {
            width: "800px",
            disableClose: true,
            data: { frage: question },
            autoFocus: false
        });

        await this.refreshQuestions();
    }
}
